<template lang="pug">
#article-carousel.news-carousel(:aria-label='t("pages.articles.carouselLabel")')
  .news-carousel__viewport(
    @mouseenter='stopAutoplay'
    @mouseleave='startAutoplay'
    @focusin='stopAutoplay'
    @focusout='startAutoplay'
  )
    article.news-carousel__slide(
      v-for="(article, index) in articles"
      :key="article.path || index"
      :class="{ 'news-carousel__slide--active': index === activeSlide }"
      :aria-hidden="index === activeSlide ? 'false' : 'true'"
      :inert='index === activeSlide ? undefined : true'
    )
      news-card(
        :article="article"
        :showText="false"
        prominent
        showOverlay
      )

  .news-carousel__controls(v-if="articles.length > 1")
    v-btn.news-carousel__arrow(
      icon="mdi-chevron-left"
      variant="text"
      color="white"
      :aria-label='t("pages.articles.previousArticle")'
      @click="showPrevious"
    )
    .news-carousel__dots(
      role="tablist"
      :aria-label='t("pages.articles.slidesLabel")'
    )
      button.news-carousel__dot(
        v-for="(article, index) in articles"
        :key="`${article.path || index}-dot`"
        type="button"
        role="tab"
        :aria-label='t("pages.articles.showSlide", { number: index + 1, title: article.title })'
        :aria-selected="index === activeSlide ? 'true' : 'false'"
        :tabindex="index === activeSlide ? '0' : '-1'"
        :class="{ 'news-carousel__dot--active': index === activeSlide }"
        @click="setSlide(index)"
      )
    v-btn.news-carousel__arrow(
      icon="mdi-chevron-right"
      variant="text"
      color="white"
      :aria-label='t("pages.articles.nextArticle")'
      @click="showNext"
    )
</template>

<script setup lang="ts">
defineOptions({
  name: 'ArticleCarousel',
})

const props = defineProps({
  articles: {
    type: Array,
    required: true,
  },
})

const activeSlide = ref(0)
const { t } = useI18n()
let autoplayTimer: ReturnType<typeof setInterval> | undefined
let reducedMotionQuery: MediaQueryList | undefined

function setSlide(index: number) {
  activeSlide.value = index
}

function showPrevious() {
  activeSlide.value =
    activeSlide.value === 0 ? props.articles.length - 1 : activeSlide.value - 1
}

function showNext() {
  activeSlide.value =
    activeSlide.value === props.articles.length - 1 ? 0 : activeSlide.value + 1
}

function startAutoplay() {
  if (props.articles.length <= 1 || reducedMotionQuery?.matches) {
    return
  }

  stopAutoplay()
  autoplayTimer = setInterval(showNext, 7000)
}

function stopAutoplay() {
  if (autoplayTimer) {
    clearInterval(autoplayTimer)
    autoplayTimer = undefined
  }
}

onMounted(() => {
  reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  startAutoplay()
})

onBeforeUnmount(() => {
  stopAutoplay()
})
</script>

<style scoped>
.news-carousel {
  display: grid;
  gap: 1rem;
}

.news-carousel__viewport {
  position: relative;
  min-height: 600px;
  overflow: hidden;
  border-radius: var(--radius-md);
}

.news-carousel__slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 220ms ease;
}

.news-carousel__slide--active {
  position: relative;
  opacity: 1;
  pointer-events: auto;
}

.news-carousel__controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.news-carousel__arrow {
  flex: 0 0 auto;
  min-width: 2.75rem;
  background: var(--color-brand-dark);
}

.news-carousel__dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  flex: 1 1 auto;
}

.news-carousel__dot {
  width: 0.8rem;
  height: 0.8rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-brand-dark) 20%, white);
  cursor: pointer;
  transition:
    transform 180ms ease,
    background-color 180ms ease;
}

.news-carousel__dot--active {
  transform: scale(1.15);
  background: var(--color-brand-accent);
}

.news-carousel__dot:focus-visible {
  outline: 2px solid var(--color-brand-accent);
  outline-offset: 3px;
}

@media (max-width: 760px) {
  .news-carousel__viewport {
    min-height: 420px;
  }

  .news-carousel__controls {
    gap: 0.75rem;
  }
}
</style>
